import React, { useState, useMemo } from "react";
import {
  Dialog,
  Box,
  Grid,
  Typography,
  Select,
  MenuItem,
  FormControl,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Backdrop,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { makeStyles } from "@material-ui/core/styles";
import moment from "moment";
import { generateSponsorQualityEvent } from "./tabDataAPI";

const useStyles = makeStyles(() => ({
  header: {
    background: "linear-gradient(180deg, #EEF2FF 0%, #F6F8FF 100%)",
    padding: "20px 24px 24px",
    textAlign: "center",
    position: "relative",
    borderBottom: "1px solid #e6e9f2",
  },
  cancel: {
    position: "absolute",
    top: 12,
    right: 16,
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  cancelText: { fontWeight: 600, fontSize: "0.85rem", color: "#1a1a2e" },
  title: { fontWeight: 700, fontSize: "1.6rem", color: "#111" },
  subtitle: { color: "#333", fontSize: "0.95rem", marginTop: 6 },
  body: { padding: "28px 40px 40px", maxWidth: 1000, margin: "0 auto", width: "100%" },
  sectionHeading: { fontWeight: 700, fontSize: "1.25rem", color: "#111" },
  sectionRule: {
    width: 34,
    height: 4,
    borderRadius: 2,
    backgroundColor: "#9aa0ad",
    margin: "6px 0 24px",
  },
  fieldLabel: { fontWeight: 700, fontSize: "0.9rem", color: "#111", marginBottom: 4 },
  fieldValue: { fontSize: "0.95rem", color: "#333" },
  remarks: {
    width: "100%",
    "& .MuiOutlinedInput-root": { borderRadius: 6 },
  },
  submitBtn: {
    backgroundColor: "#EB5E28",
    color: "#fff",
    textTransform: "none",
    borderRadius: "2rem",
    minWidth: 320,
    height: 46,
    fontSize: "0.95rem",
    fontWeight: 600,
    "&:hover": { backgroundColor: "#000", color: "#fff" },
    "&.Mui-disabled": { backgroundColor: "#EB5E28", color: "#fff", opacity: 0.6 },
  },
  // Success modal
  successPaper: { borderRadius: 12, padding: "36px 44px", textAlign: "center", maxWidth: 640 },
  successTitle: { fontWeight: 700, fontSize: "1.5rem", color: "#111", margin: "18px 0 24px", lineHeight: 1.3 },
  okBtn: {
    backgroundColor: "#EB5E28",
    color: "#fff",
    textTransform: "none",
    borderRadius: "2rem",
    minWidth: 90,
    fontWeight: 600,
    "&:hover": { backgroundColor: "#000", color: "#fff" },
  },
  // Outlined "View XML for Submission / Response" actions flanking Ok.
  xmlOutlineBtn: {
    textTransform: "none",
    borderRadius: "2rem",
    border: "1px solid #1a1a2e",
    color: "#1a1a2e",
    backgroundColor: "#fff",
    fontWeight: 600,
    fontSize: "0.78rem",
    padding: "6px 16px",
    whiteSpace: "nowrap",
    "&:hover": { backgroundColor: "#f0f0f5", borderColor: "#1a1a2e" },
  },
  successActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
    flexWrap: "wrap",
  },
  // XML viewer modal
  xmlDialogPaper: { borderRadius: 10 },
  xmlHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 20px",
    borderBottom: "1px solid #e6e9f2",
  },
  xmlTitle: { fontWeight: 700, fontSize: "1.05rem", color: "#111" },
  xmlPre: {
    margin: 0,
    padding: "18px 22px",
    fontFamily: "'Consolas','Menlo','Monaco','Courier New',monospace",
    fontSize: "0.82rem",
    lineHeight: 1.55,
    color: "#2d2d2d",
    backgroundColor: "#f7f8fb",
    whiteSpace: "pre",
    overflow: "auto",
    maxHeight: "62vh",
  },
  // Full-screen blocking overlay shown while the QE generation API is in flight.
  loadingBackdrop: {
    zIndex: 2000,
    color: "#fff",
    flexDirection: "column",
  },
  loadingText: {
    marginTop: 16,
    fontWeight: 600,
    fontSize: "1rem",
  },
}));

const OWNER_OPTIONS = [
  "William Johnson",
  "Sarah Chen",
  "Michael Brown",
  "Emily Davis",
  "David Wilson",
];

// Veeva Vault "Create Quality Event" payloads shown from the success modal.
// Static demo representations of what is sent to / returned by the sponsor QMS.
const SUBMISSION_XML = `<?xml version="1.0" encoding="UTF-8"?>
<vault_request>
  <record>
    <title__v>Temperature excursion – Cold Room 3</title__v>
    <quality_event_type__v>deviation__c</quality_event_type__v>
    <description__c>Cold room exceeded 8°C for 42 min on 21-Jun-2026 during batch CHO-CC-2411 staging.</description__c>
    <date_detected__c>2026-06-21</date_detected__c>
    <severity__c>major__c</severity__c>
    <site__v>OOW000000000301</site__v>
    <department__c>OOW000000000077</department__c>
    <owner__v>V0U000000000123</owner__v>
    <due_date__c>2026-07-21</due_date__c>
    <external_id__c>CBX-EVT-558931</external_id__c>
  </record>
</vault_request>`;

const RESPONSE_XML = `<?xml version="1.0" encoding="UTF-8"?>
<vault_response>
  <responseStatus>SUCCESS</responseStatus>
  <data>
    <record>
      <responseStatus>SUCCESS</responseStatus>
      <data>
        <id>OOW000000000945</id>
        <url>/api/v26.1/vobjects/quality_event__v/OOW000000000945</url>
        <name__v>QE-2026-001042</name__v>
        <event>created__sys</event>
      </data>
    </record>
  </data>
</vault_response>`;

const fmtDate = (v) =>
  v && moment(v).isValid() ? moment(v).format("DD-MMM-YYYY").toUpperCase() : v || "-";

const Field = ({ label, value, classes }) => (
  <Box style={{ marginBottom: 28 }}>
    <Typography className={classes.fieldLabel}>{label}</Typography>
    <Typography className={classes.fieldValue}>{value || "-"}</Typography>
  </Box>
);

/**
 * Create Quality Event form. Opens from the Deviations tab "Generate Deviation"
 * action (sponsor view). Fields are auto-populated from the deviation; only
 * Assigned Owner and Due Date are editable. Submitting simulates the QMS
 * (Veeva Vault) call and, on success, returns the generated sponsor QE id.
 */
const CreateQualityEventDialog = ({ open, onClose, deviation, onSubmitted }) => {
  const classes = useStyles();
  const dev = deviation || {};

  // Auto-populated QE details (real deviation data first, demo values fill gaps).
  const details = useMemo(
    () => ({
      bmsDeviationId: dev.deviationNumber || "QE-2026-0005",
      qeTitle: dev.deviationTitle || dev.qeTitle || "Temperature Excursion - Cold Room 3",
      qeType: dev.deviationType || "Process",
      description:
        dev.initialDescription || "Cold room exceeded 8°C for 42 min on 21-Jun.",
      detectionDate: dev.reportingDate ? fmtDate(dev.reportingDate) : "23-JAN-2026",
      severity: dev.deviationCategory || "Major",
      site: dev.site || "Greenville",
      warehouse: dev.warehouse || "Cold Chain",
      externalId: dev.externalId || "CBX-EVT-558931",
    }),
    [dev]
  );

  const [assignedOwner, setAssignedOwner] = useState(OWNER_OPTIONS[0]);
  const [dueDate, setDueDate] = useState(
    moment().add(5, "months").format("YYYY-MM-DD")
  );
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null); // holds generated response
  const [xmlView, setXmlView] = useState(null); // null | 'submission' | 'response'

  const handleClose = () => {
    if (submitting) return;
    setSuccess(null);
    onClose && onClose();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Simulated QMS (Veeva Vault) call — fields sent to the sponsor system.
      const res = await generateSponsorQualityEvent({
        bmsDeviationId: details.bmsDeviationId,
        qeTitle: details.qeTitle,
        qeType: details.qeType,
        description: details.description,
        detectionDate: details.detectionDate,
        severity: details.severity,
        site: details.site,
        warehouse: details.warehouse,
        externalId: details.externalId,
        assignedOwner,
        dueDate,
        remarks,
      });
      setSuccess(res);
    } catch (e) {
      setSuccess({
        status: true,
        sponsorDeviationId: `SP-${details.bmsDeviationId}`,
        sponsorDeviationStatus: "New",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleOk = () => {
    const generated = success || {};
    setSuccess(null);
    setXmlView(null);
    onSubmitted &&
      onSubmitted({
        sponsorDeviationId:
          generated.sponsorDeviationId || `SP-${details.bmsDeviationId}`,
        sponsorDeviationStatus: generated.sponsorDeviationStatus || "New",
      });
    onClose && onClose();
  };

  return (
    <>
      {/* Blocks all interaction and shows a visible loader during the QE API call */}
      <Backdrop className={classes.loadingBackdrop} open={submitting}>
        <CircularProgress color="inherit" />
        <Typography className={classes.loadingText}>
          Submitting to Sponsor QMS...
        </Typography>
      </Backdrop>

      <Dialog open={open} onClose={handleClose} fullScreen>
        {/* Header */}
        <Box className={classes.header}>
          <Box className={classes.cancel}>
            <Typography className={classes.cancelText}>Cancel</Typography>
            <IconButton size="small" onClick={handleClose} disabled={submitting}>
              <CloseIcon style={{ color: "#e53935" }} />
            </IconButton>
          </Box>
          <Typography className={classes.title}>Create Quality Event</Typography>
          <Typography className={classes.subtitle}>
            Quality Event Name, Type, Description, and other fields that are required
            for QE Creation.
          </Typography>
        </Box>

        {/* Body */}
        <Box className={classes.body}>
          <Typography className={classes.sectionHeading}>QE Details</Typography>
          <Box className={classes.sectionRule} />

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Field label="BMS Deviation ID" value={details.bmsDeviationId} classes={classes} />
              <Field label="QE Type" value={details.qeType} classes={classes} />
              <Field label="Detection Date" value={details.detectionDate} classes={classes} />
              <Field label="Site" value={details.site} classes={classes} />

              {/* Assigned Owner — editable */}
              <Box style={{ marginBottom: 28 }}>
                <Typography className={classes.fieldLabel}>Assigned Owner</Typography>
                <FormControl variant="outlined" size="small" style={{ minWidth: 260 }}>
                  <Select
                    value={assignedOwner}
                    onChange={(e) => setAssignedOwner(e.target.value)}
                  >
                    {OWNER_OPTIONS.map((o) => (
                      <MenuItem key={o} value={o}>
                        {o}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Field label="External ID" value={details.externalId} classes={classes} />
            </Grid>

            <Grid item xs={12} md={6}>
              <Field label="QE Title" value={details.qeTitle} classes={classes} />
              <Field label="Description" value={details.description} classes={classes} />
              <Field label="Severity" value={details.severity} classes={classes} />
              <Field label="Warehouse" value={details.warehouse} classes={classes} />

              {/* Due Date — editable */}
              <Box style={{ marginBottom: 28 }}>
                <Typography className={classes.fieldLabel}>Due Date</Typography>
                <TextField
                  type="date"
                  size="small"
                  variant="outlined"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  style={{ minWidth: 260 }}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            </Grid>
          </Grid>

          <Box style={{ borderTop: "1px solid #e0e0e0", margin: "12px 0 28px" }} />

          <Typography className={classes.sectionHeading}>Other Information</Typography>
          <Box className={classes.sectionRule} />

          <TextField
            className={classes.remarks}
            variant="outlined"
            placeholder="Enter additional remarks if any"
            multiline
            minRows={7}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
          />

          <Box style={{ textAlign: "center", marginTop: 40 }}>
            <Button
              className={classes.submitBtn}
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <CircularProgress size={18} style={{ color: "#fff", marginRight: 10 }} />
                  Submitting to Sponsor QMS...
                </>
              ) : (
                "Submit for Quality Event Generation"
              )}
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Success modal */}
      <Dialog
        open={!!success}
        onClose={handleOk}
        maxWidth="sm"
        PaperProps={{ className: classes.successPaper }}
      >
        <Box style={{ display: "flex", justifyContent: "center" }}>
          <CheckCircleIcon style={{ fontSize: 64, color: "#1e8e3e" }} />
        </Box>
        <Typography className={classes.successTitle}>
          Quality Incidence Generated
        </Typography>
        <Grid container spacing={2} justify="center" style={{ marginBottom: 28 }}>
          <Grid item xs={4} style={{ textAlign: "center" }}>
            <Typography style={{ fontWeight: 700, fontSize: "0.9rem" }}>
              Sponsor Deviation ID
            </Typography>
            <Typography style={{ fontSize: "0.95rem", marginTop: 6 }}>
              {details.bmsDeviationId}
            </Typography>
          </Grid>
          <Grid item xs={4} style={{ textAlign: "center" }}>
            <Typography style={{ fontWeight: 700, fontSize: "0.9rem" }}>QE Type</Typography>
            <Typography style={{ fontSize: "0.95rem", marginTop: 6 }}>
              {details.qeType}
            </Typography>
          </Grid>
          <Grid item xs={4} style={{ textAlign: "center" }}>
            <Typography style={{ fontWeight: 700, fontSize: "0.9rem" }}>Severity</Typography>
            <Typography style={{ fontSize: "0.95rem", marginTop: 6 }}>
              {details.severity}
            </Typography>
          </Grid>
        </Grid>
        <Box className={classes.successActions}>
          <Button
            className={classes.xmlOutlineBtn}
            onClick={() => setXmlView("submission")}
          >
            View Message to QMS
          </Button>
          <Button className={classes.okBtn} onClick={handleOk}>
            Ok
          </Button>
          <Button
            className={classes.xmlOutlineBtn}
            onClick={() => setXmlView("response")}
          >
            View Response From QMS
          </Button>
        </Box>
      </Dialog>

      {/* XML viewer — opens on top of the success modal for Submission / Response */}
      <Dialog
        open={!!xmlView}
        onClose={() => setXmlView(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ className: classes.xmlDialogPaper }}
      >
        <Box className={classes.xmlHeader}>
          <Typography className={classes.xmlTitle}>
            {xmlView === "response" ? "XML For Response" : "XML For Submission"}
          </Typography>
          <IconButton size="small" onClick={() => setXmlView(null)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <pre className={classes.xmlPre}>
          {xmlView === "response" ? RESPONSE_XML : SUBMISSION_XML}
        </pre>
      </Dialog>
    </>
  );
};

export default CreateQualityEventDialog;
